// React context for the current funnel phase. Lets phase-aware blocks
// (siteNav, footer, etc.) swap content based on where the user is in the
// flow without prop-drilling through every layer.
//
// Wrap the block tree with `<PhaseProvider phase={state.phase}>` once at
// the root (App.tsx etc.) — siteNav et al. consume `usePhase()` and pick
// the right CTA / copy variant.

import { createContext, useContext, type ReactNode } from 'react';

/** Funnel phases. Mirror App.tsx's Phase union — kept narrow on purpose
 *  so blocks only need to handle the phases that ever render under them. */
export type FunnelPhase =
  | 'intro'
  | 'email-gate'
  | 'upgrade'
  | 'gate'
  | 'questions'
  | 'submitting'
  | 'result'
  | 'resuming'
  | 'external-redirect'
  | 'loading'
  | 'error';

const PhaseCtx = createContext<FunnelPhase | null>(null);

export function PhaseProvider({
  phase,
  children,
}: {
  phase: FunnelPhase | null | undefined;
  children: ReactNode;
}) {
  return <PhaseCtx.Provider value={phase ?? null}>{children}</PhaseCtx.Provider>;
}

/** Read the current funnel phase. null when no phase is in context (e.g.
 *  on a non-flow page). Block renderers should treat null as "phase
 *  unknown — render the default cta/content". */
export function usePhase(): FunnelPhase | null {
  return useContext(PhaseCtx);
}
